/**
 * Section 5: Photo Submission - General Interior/Exterior Photos
 */
/* eslint-disable react/jsx-no-bind */

import { Fragment } from 'preact';
import { useCallback } from 'preact/hooks';
import { useForm } from '@ox/visits-ui/context/FormContext';
import { Select } from '@ox/visits-ui/components/shared/FormComponents';
import { FileUpload } from '@ox/visits-ui/components/shared/FileUpload';
import { PHOTO_GROUPS, QC_COMBINED_PHOTO_SLOTS } from '@ox/visits-ui/config/qcPhotoSlots';

interface Section5QCPhotoUploadProps {
  /** Combined (QC Standard) variant: adds the grouped named photo slots and conditional gates. */
  combined?: boolean;
}

export function Section5QCPhotoUpload({ combined }: Section5QCPhotoUploadProps) {
  const { formData, updateField } = useForm();

  const handleGateChange = useCallback(
    (field: string, value: string) => updateField(field, value),
    [updateField],
  );

  return (
    <div className="form-section">
      <h2>Photo Submission</h2>
      <div className="property-value-display">
        <a href="/guidance?checklist=photo" target="_blank"><p>📋 View full photo checklist</p></a>
      </div>

      <hr className="section-divider" />

      {combined ? (
        PHOTO_GROUPS.map(({ key, heading }) => (
          <div className="photo-group" key={key}>
            <h3>{heading}</h3>
            {QC_COMBINED_PHOTO_SLOTS
              .filter((slot) => slot.group === key)
              .map((slot) => {
                const gateMet = !slot.conditionField || formData[slot.conditionField] === slot.conditionValue;
                return (
                  <Fragment key={slot.mediaType}>
                    {/* Inline Yes/No gate for slots whose condition is answered here. */}
                    {slot.conditionLabel && slot.conditionField && (
                      <Select
                        label={slot.conditionLabel}
                        name={slot.conditionField}
                        value={formData[slot.conditionField] || ''}
                        onChange={(value) => handleGateChange(slot.conditionField as string, value)}
                        options={[
                          { label: 'Select...', value: '', disabled: true },
                          { label: 'Yes', value: 'Yes' },
                          { label: 'No', value: 'No' },
                        ]}
                        required
                      />
                    )}
                    {gateMet && (
                      <FileUpload
                        label={slot.label}
                        mediaType={slot.mediaType}
                        sectionNumber={5}
                        minFiles={slot.minFiles}
                        maxFiles={slot.maxFiles}
                        helpText={slot.helpText}
                        accept="image/*"
                        required={(slot.minFiles ?? 0) > 0}
                      />
                    )}
                  </Fragment>
                );
              })}
          </div>
        ))
      ) : (
        <>
          <FileUpload
            label="Street View of Property Front (1 Photo)"
            mediaType="photo_street_front"
            sectionNumber={5}
            accept="image/*"
            helpText="Photo needs to include street view of property front"
            required
          />
          <FileUpload
            label="Please provide any additional condition photos you may have taken during your visit"
            mediaType="photos_interior"
            sectionNumber={5}
            minFiles={1}
            maxFiles={100}
            accept="image/*"
            required
          />
        </>
      )}
    </div>
  );
}
